import { useEffect, useState } from "react";
import { Star, MessageSquarePlus } from "lucide-react";
import { API } from "@/lib/api";
import ReviewSubmitModal from "@/components/ReviewSubmitModal";

function StaticStars({ rating, size = "w-4 h-4" }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${size} ${
            rating >= n ? "text-[#FBBF24] fill-[#FBBF24]" : "text-zinc-300"
          }`}
        />
      ))}
    </div>
  );
}

export default function ReviewsList({
  scope = "app",
  targetId = null,
  limit = 6,
  heading = "What students say",
  emptyLabel = "Be the first to leave a review.",
  compact = false,
}) {
  const [data, setData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams({ scope, limit: String(limit) });
    if (targetId) params.set("target_id", targetId);
    fetch(`${API}/reviews?${params.toString()}`)
      .then((r) => (r.ok ? r.json() : { items: [], total: 0, average: 0 }))
      .then(setData)
      .catch(() => setData({ items: [], total: 0, average: 0 }));
  }, [scope, targetId, limit, refreshTick]);

  if (!data) return null;
  const items = data.items || [];

  return (
    <section
      data-testid={scope === "note" ? "note-reviews" : "app-reviews"}
      className={compact ? "" : "max-w-7xl mx-auto px-6 py-16"}
    >
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">/ Reviews</p>
          <h2 className={`mt-2 font-heading font-black tracking-tight ${compact ? "text-2xl" : "text-4xl sm:text-5xl"}`}>
            {heading}
          </h2>
          {data.total > 0 && (
            <div className="mt-3 flex items-center gap-3">
              <StaticStars rating={Math.round(data.average)} size="w-5 h-5" />
              <span className="font-heading font-black text-xl">{data.average.toFixed(1)}</span>
              <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
                · {data.total} review{data.total === 1 ? "" : "s"}
              </span>
            </div>
          )}
        </div>
        <button
          data-testid="open-review-modal"
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-3 bg-black text-white btn-brutal"
        >
          <MessageSquarePlus className="w-4 h-4" /> Leave a review
        </button>
      </div>

      {items.length === 0 ? (
        <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">{emptyLabel}</p>
      ) : (
        <div className={`grid gap-4 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
          {items.map((r) => (
            <blockquote
              key={r.id}
              data-testid={`review-card-${r.id}`}
              className="bg-white border-2 border-black p-5 shadow-[4px_4px_0_0_#000]"
            >
              <StaticStars rating={r.rating} />
              {r.text && (
                <p className="mt-3 text-sm leading-relaxed text-zinc-800">&ldquo;{r.text}&rdquo;</p>
              )}
              <footer className="mt-4 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                — {r.name}
              </footer>
            </blockquote>
          ))}
        </div>
      )}

      {showModal && (
        <ReviewSubmitModal
          scope={scope}
          targetId={targetId}
          onClose={() => setShowModal(false)}
          onSubmitted={() => setRefreshTick((t) => t + 1)}
        />
      )}
    </section>
  );
}
